import { Box } from '@mui/material';
import FormFields from '../../../../form/FormFields';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import StartIcon from '@mui/icons-material/Start';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ExoDraftManager from '../../../ExoDraftManager';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import moment from 'moment';
import { useEffect, useState, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useConfig, DBRequest } from '../../../../../api/api';
import ParticipantsBlockTimesPopper from '../../ParticipantsBlockTimesPopper';

export const CalMeetingForm = ({
  dataCollection,
  onChange,
  calendarOptions,
  onValidationChange,
  openBlock,
  setOpenBlock,
}) => {
  const { t } = useTranslation();

  const [lastChange, setLastChange] = useState({});
  const [debouncedDataCollection] = useDebounce(lastChange, 700);

  const config = useConfig();

  // popper
  const [popperAnchor, setPopperAnchor] = useState(null);

  function handleOpenPopper(event) {
    setPopperAnchor(event.currentTarget);
    setOpenBlockedTimes(!openBlockedTimes);
    setOpenBlock(!openBlock);
  }

  // show/hide Blocked times of user
  const [openBlockedTimes, setOpenBlockedTimes] = useState(false);
  const [blockedUserTimesData, setBlockedUserTimesData] = useState([]);

  // Bug 12: Track last conflict count to prevent infinite loop
  const lastConflictCountRef = useRef(null);

  useEffect(() => {
    if (dataCollection.participants) {
      handleLoadUsersBlockedTimes(
        dataCollection.startDate,
        dataCollection.endDate,
        dataCollection.participants,
      );
    }
  }, [
    openBlockedTimes,
    dataCollection.participants,
    // Bug 12: Removed participantConflictsCount from dependencies to prevent infinite loop
    dataCollection.startTime,
    dataCollection.startDate,
    dataCollection.endDate,
    dataCollection.endTime,
    config,
  ]);

  function handleLoadUsersBlockedTimes(startDate, endDate, participants) {
    const dateStart = moment(startDate).format('YYYY-MM-DD');
    const dateEnd = moment(endDate).format('YYYY-MM-DD');

    if (participants?.length === 0) {
      setBlockedUserTimesData([]); // No participants, clear data
      onChange({ participantConflictsCount: 0 }); // Reset conflict count
      return;
    }

    //  Extract IDs only from participants with type "user"
    const userIds = participants
      ?.filter((p) => p.type === 'user' && p.participant?.id)
      .map((p) => `users[]=${p.participant.id}`)
      .join('&');

    if (!userIds) return;

    const path = `blocked-times?startDate=${dateStart}&endDate=${dateEnd}&${userIds}`;
    DBRequest({
      config,
      path,
      onResponse: (res) => {
        const blockedData = res.data;

        setBlockedUserTimesData(blockedData);

        // Check for conflicts
        const conflictCount = calculateConflicts(
          blockedData,
          dataCollection.startTime,
          dataCollection.endTime,
        );

        // Bug 12: Update the count only if changed and different from last tracked value
        if (
          dataCollection.participantConflictsCount !== conflictCount &&
          lastConflictCountRef.current !== conflictCount
        ) {
          lastConflictCountRef.current = conflictCount;
          onChange({
            participantConflictsCount: conflictCount,
          });
        }
      },
    });
  }

  // To detect conflicts
  function calculateConflicts(blockedTimesData, meetingStart, meetingEnd) {
    const start = moment(meetingStart);
    const end = moment(meetingEnd);

    if (!start.isValid() || !end.isValid()) return 0;

    let conflictCount = 0;

    for (const user of blockedTimesData) {
      const userBlockedTimes = user.blockedTimes || [];

      for (const blocked of userBlockedTimes) {
        // If wholeDay is true, count as conflict immediately
        if (blocked.wholeDay) {
          conflictCount++;
          break; // Only count once per user
        }

        const blockStart = moment(blocked.startTime);
        const blockEnd = moment(blocked.endTime);

        // If either invalid, skip
        if (!blockStart.isValid() || !blockEnd.isValid()) continue;

        const overlaps = blockStart.isBefore(end) && blockEnd.isAfter(start); // overlap check

        if (overlaps) {
          conflictCount++;
          break; // Only count once per user
        }
      }
    }

    return conflictCount;
  }

  const fieldsWholeDay = [
    {
      label: t('Name'),
      key: 'label',
      type: 'text',
      colspan: 2,
      textBlockType: 'meetingName',
    },

    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
      colspan: 1,
      Icon: StartIcon,
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
      colspan: 1,
      Icon: KeyboardTabIcon,
    },
    {
      label: t('Whole Day'),
      key: 'wholeDay',
      type: 'checkbox',
      colspan: 2,
      Icon: DateRangeIcon,
    },

    {
      label: t('Online Meeting'),
      key: 'onlineMeeting',
      type: 'meeting',
      Icon: VideocamIcon,
      colspan: 2,
    },
    {
      label: t('Location'),
      key: 'location',
      type: 'address',
      Icon: LocationOnIcon,
      colspan: 2,
      allowOwnAddress: true,
      suggestionsArr: [
        {
          key: 'participants',
          isArray: true,
          type: 'participant',
        },
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Participants'),
      key: 'participants',
      type: 'participantsAutoComplete',
      colspan: 2,
      searchParams: [
        'companies',
        'contacts',
        'person_leads',
        'company_leads',
        'users',
      ],
      suggestionsArr: [
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
      showHideParticipation: true,
      button: {
        onClick: (event) => {
          handleOpenPopper(event);
        },
        Icon: EventBusyIcon,
        badge: {
          badgeFunction: (dataCollection) =>
            dataCollection?.participantConflictsCount,
          badgeColor: 'warning',
        },
      },
    },
    {
      label: t('Project'),
      key: 'project',
      type: 'project',
      colspan: 2,
      apiUrl: 'projects',
    },
    {
      label: t('Category'),
      key: 'category',
      type: 'eventCategory',
      colspan: 2,
    },
    {
      label: t('Agenda'),
      key: 'agenda',
      type: 'richText',
      textBlockType: 'meetingAgenda',
      colspan: 2,
      Icon: FormatListNumberedIcon,
    },
    {
      label: t('more options'),
      type: 'expandFields',
      colspan: 2,
      fields: [
        {
          label: t('Partner'),
          key: 'meetingPartner',
          type: 'autoCompleteClient',
          colspan: 2,
          searchParams: [
            'companies',
            'contacts',
            'person_leads',
            'company_leads',
            'users',
          ],
        },

        {
          label: t('Owner'),
          key: 'owner',
          type: 'user',
          colspan: 2,
        },
        {
          label: t('Reminder to Receiver'),
          key: 'reminders',
          type: 'reminder',
          colspan: 2,
          dateKey: 'startDate',
          timeKey: 'startTime',
          size: 'small',
        },
        {
          label: t('Meeting is already accepted'),
          key: 'meetingIsConfirmed',
          type: 'checkbox',
          colspan: 2,
          Icon: DoneAllIcon,
        },
        {
          label: t('Block time period for all participating employees'),
          key: 'blockTime',
          type: 'checkbox',
          colspan: 2,
          Icon: SafetyCheckIcon,
        },
        {
          label: t('Internal note'),
          key: 'internalNote',
          type: 'richText',
          colspan: 2,
          Icon: EditNoteIcon,
        },
      ],
    },
  ];
  const validationSchemaWholeDay = Yup.object().shape({
    startDate: Yup.string().required(t('Is required')),
    endDate: Yup.string().required(t('Is required')),
    label: Yup.string().required(t('Is required')),
    /*onlineMeeting: Yup.object()
      .shape({
        type: Yup.string().nullable(),
        url: Yup.string().url().nullable(),
      })
      .nullable(),*/
    owner: Yup.object().shape({ id: Yup.string().required() }),
    location: Yup.object()
      .shape({
        street: Yup.string(),
        houseNumber: Yup.string(),
        zipCode: Yup.string(),
        city: Yup.string(),
        countryCode: Yup.string(),
      })
      .nullable(),
  });

  const fieldsHalfDay = [
    {
      label: t('Name'),
      key: 'label',
      type: 'text',
      colspan: 2,
      textBlockType: 'meetingName',
    },
    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
      colspan: 1,
      Icon: StartIcon,
    },
    {
      label: t('Start Time'),
      key: 'startTime',
      type: 'time',
      colspan: 1,
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
      colspan: 1,
      Icon: KeyboardTabIcon,
    },
    {
      label: t('End Time'),
      key: 'endTime',
      type: 'time',
      colspan: 1,
    },
    {
      label: t('Whole Day'),
      key: 'wholeDay',
      type: 'checkbox',
      colspan: 2,
      Icon: DateRangeIcon,
    },

    {
      label: t('Online Meeting'),
      key: 'onlineMeeting',
      type: 'meeting',
      Icon: VideocamIcon,
      colspan: 2,
    },
    {
      label: t('Location'),
      key: 'location',
      type: 'address',
      Icon: LocationOnIcon,
      colspan: 2,
      allowOwnAddress: true,
      suggestionsArr: [
        {
          key: 'participants',
          isArray: true,
          type: 'participant',
        },
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Participants'),
      key: 'participants',
      type: 'participantsAutoComplete',
      colspan: 2,
      searchParams: [
        'companies',
        'contacts',
        'person_leads',
        'company_leads',
        'users',
      ],
      suggestionsArr: [
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
      showHideParticipation: true,
      button: {
        onClick: (event) => {
          handleOpenPopper(event);
        },
        Icon: EventBusyIcon,
        badge: {
          badgeFunction: (dataCollection) =>
            dataCollection?.participantConflictsCount,
          badgeColor: 'warning',
        },
      },
    },
    {
      label: t('Project'),
      key: 'project',
      type: 'project',
      colspan: 2,
      apiUrl: 'projects',
    },
    {
      label: t('Category'),
      key: 'category',
      type: 'eventCategory',
      colspan: 2,
    },

    {
      label: t('Agenda'),
      key: 'agenda',
      type: 'richText',
      colspan: 2,
      Icon: FormatListNumberedIcon,
    },

    {
      label: t('more options'),
      type: 'expandFields',
      colspan: 2,
      fields: [
        {
          label: t('Partner'),
          key: 'meetingPartner',
          type: 'autoCompleteClient',
          colspan: 2,
          searchParams: [
            'companies',
            'contacts',
            'person_leads',
            'company_leads',
            'users',
          ],
        },

        {
          label: t('Owner'),
          key: 'owner',
          type: 'user',
          colspan: 2,
        },
        {
          label: t('Reminder to Receiver'),
          key: 'reminders',
          type: 'reminder',
          colspan: 2,
          dateKey: 'startDate',
          timeKey: 'startTime',
          size: 'small',
        },
        {
          label: t('Meeting is already accepted'),
          key: 'meetingIsConfirmed',
          type: 'checkbox',
          colspan: 2,
          Icon: DoneAllIcon,
        },
        {
          label: t('Block time period for all participating employees'),
          key: 'blockTime',
          type: 'checkbox',
          colspan: 2,
          Icon: SafetyCheckIcon,
        },
        {
          label: t('Internal note'),
          key: 'internalNote',
          type: 'richText',
          colspan: 2,
          Icon: EditNoteIcon,
        },
      ],
    },
  ];
  const validationSchemaHalfDay = Yup.object().shape({
    startDate: Yup.string().required(t('Is required')),
    startTime: Yup.string().required(t('Is required')),
    endDate: Yup.string().required(t('Is required')),
    endTime: Yup.string().required(t('Is required')),
    label: Yup.string().required(t('Is required')),
    /*onlineMeeting: Yup.object()
      .shape({
        type: Yup.string().nullable(),
        url: Yup.string().url().nullable(),
      })
      .nullable(),*/
    location: Yup.object()
      .shape({
        street: Yup.string(),
        houseNumber: Yup.string(),
        zipCode: Yup.string(),
        city: Yup.string(),
        countryCode: Yup.string(),
      })
      .nullable(),
  });

  useEffect(() => {
    validateValues(debouncedDataCollection);
  }, [debouncedDataCollection]);

  function validateValues(last) {
    const { data, change } = last;
    if (!data || !data.startDate || !data.endDate) return;

    const corrections = {};

    // check if startDate is before endDate
    const startDate = moment(data.startDate);
    const endDate = moment(data.endDate);

    if (startDate.isAfter(endDate)) {
      if (change.startDate) corrections.endDate = data.startDate;
      if (change.endDate) corrections.startDate = data.endDate;
    }

    // time if its the same day
    const startTime = moment(data.startTime);
    const endTime = moment(data.endTime);

    if (startDate.isSame(endDate, 'day') && startTime.isAfter(endTime)) {
      if (change.startTime)
        corrections.endTime = startTime.clone().add(30, 'm').toISOString();
      if (change.endTime)
        corrections.startTime = endTime.clone().subtract(30, 'm').toISOString();
    }

    // Only call onChange when there are actual corrections
    if (Object.keys(corrections).length > 0) {
      onChange(corrections);
    }
  }

  return (
    <>
      <Box className="flex flex-row lg:gap-3 w-full h-full overflow-hidden">
        <Box className="h-full w-full flex flex-col overflow-y-auto px-3 py-2 justify-start">
          <Box className="grid grid-cols-2 gap-3 w-full ">
            <ExoDraftManager
              draftType="meeting"
              titleFunction={(e) => e.label}
              dataCollection={dataCollection}
              setDataCollection={onChange}
              onChange={onChange}
            />
            <FormFields
              fields={
                dataCollection.wholeDay == '1' ? fieldsWholeDay : fieldsHalfDay
              }
              dataCollection={dataCollection}
              updateDataCollection={(data) => {
                setLastChange({
                  change: data.reminders ? lastChange.change : data,
                  data: { ...dataCollection, ...data },
                });
                onChange(data);
              }}
              validationSchema={
                dataCollection.wholeDay == '1'
                  ? validationSchemaWholeDay
                  : validationSchemaHalfDay
              }
              size={'small'}
              onValidationChange={onValidationChange}
            />
          </Box>
        </Box>
        {/* Blocked times of participants */}
        <ParticipantsBlockTimesPopper
          open={openBlockedTimes}
          anchorEl={popperAnchor}
          onClose={() => {
            setOpenBlockedTimes(false);
            setOpenBlock(false);
          }}
          dataCollection={dataCollection}
          blockedUserTimesData={blockedUserTimesData}
          onChange={onChange}
        />
      </Box>
    </>
  );
};
